/**
 * featureGates — configuración central del feature-gating por tier (NER-3161 / spec NER-3149).
 *
 * Fuente de verdad de gating en runtime = `subscription.limits.{xxxEnabled}` (+ grandfather).
 * Este archivo sólo cubre el PRE-CHECK del FE (useFeatureGate) y la resolución de copy/CTA
 * del FeatureGateModal. El 403 `FEATURE_GATE_REJECTED` del backend es la autoridad final:
 * el modal driven-by-interceptor usa el payload del 403 tal cual (requiredPlan/addonId/...).
 *
 * Mantener sync con:
 *  - services/subscriptions/gating-spec.md (secciones 2A, 4, 9)
 *  - services/.../middleware/checkSubscriptionLimit.js (checkFeatureEnabled)
 *  - AddonsSection.js NATIVE_PLAN_BY_FLAG
 */

// Evento global que dispara el interceptor de api.js cuando el backend rechaza con
// 403 FEATURE_GATE_REJECTED. FeatureGateProvider lo escucha y abre el modal.
export const FEATURE_GATE_EVENT = 'feature-gate-rejected';
export const FEATURE_GATE_ERROR_CODE = 'FEATURE_GATE_REJECTED';

// Jerarquía de planes. Los alias en castellano ('profesional') mapean al canónico FE
// ('professional') para que nunca rompa el display ni la comparación de niveles.
export const PLAN_ORDER = { free: 1, starter: 2, professional: 3, business: 4, enterprise: 5 };
const PLAN_ALIASES = { profesional: 'professional' };

export const canonicalPlan = (planId) => {
  if (!planId) return planId;
  const p = String(planId).toLowerCase();
  return PLAN_ALIASES[p] || p;
};

export const planLevel = (planId) => PLAN_ORDER[canonicalPlan(planId)] || 0;

// Normaliza cualquier forma del nombre de feature al flag completo (`kdsEnabled`).
export const toFeatureFlag = (feature) => {
  if (!feature) return feature;
  return /Enabled$/.test(feature) ? feature : `${feature}Enabled`;
};

// Nombre base (sin sufijo Enabled), usado como clave de i18n (`featureGate.features.kds`).
export const toFeatureBase = (feature) => (feature ? feature.replace(/Enabled$/, '') : feature);

// Tier mínimo por feature (spec NER-3149 §2A). Alineado a los planId reales del seed.
// `paymentIntegrations` y `smartMenuPromos` NO se listan: son universal_free (sign-off Diego).
export const FEATURE_MIN_PLAN = {
  // Sprint 1 — Profesional+
  kdsEnabled: 'professional',
  floorPlanEnabled: 'professional',
  cashRegisterEnabled: 'professional',
  afipInvoicingEnabled: 'professional',
  inventoryEnabled: 'professional',
  crmEnabled: 'professional',
  suppliersEnabled: 'professional',
  advancedReportsEnabled: 'professional',
  dataExportEnabled: 'professional',
  promotionsEnabled: 'professional',
  // Sprint 2 — Business+
  deliveryEnabled: 'business',
  customerAppEnabled: 'business',
  waitingQueueEnabled: 'business',
  recipesEnabled: 'business',
  // Sprint 3 — Starter+
  takeawayEnabled: 'starter',
  // Sprint 4 — Enterprise / Business
  multiRestaurantEnabled: 'enterprise',
  whitelabelEnabled: 'enterprise',
  privateAPIEnabled: 'business',
};

// Features comprables como add-on desde un plan inferior (spec §2A columna "Add-on").
// `id` es best-effort para el pre-check del hook; el 403 del backend manda el `addonId`
// autoritativo. `availableFrom` = plan mínimo desde el que se puede comprar el add-on.
export const FEATURE_ADDON = {
  afipInvoicingEnabled: { id: 'afip-invoicing', availableFrom: 'starter' },
  deliveryEnabled: { id: 'delivery', availableFrom: 'professional' },
  customerAppEnabled: { id: 'customer-app', availableFrom: 'professional' },
  recipesEnabled: { id: 'recipes', availableFrom: 'professional' },
  multiRestaurantEnabled: { id: 'multi-restaurant', availableFrom: 'professional' },
};

/**
 * Evalúa el estado de gating de una feature contra una subscription (pre-check FE).
 * @param {string} feature - nombre lógico o flag (`kds` | `kdsEnabled`)
 * @param {object} subscription - record de SubscriptionContext
 * @returns {{enabled, requiresUpgrade, isGrandfathered, requiredPlan, addonAvailable, addonId, currentPlan, feature}}
 */
export const evaluateFeatureGate = (feature, subscription) => {
  const flag = toFeatureFlag(feature);
  const limits = subscription?.limits || {};
  const grandfathered = subscription?.grandfatheredFeatures || [];
  const currentPlan = canonicalPlan(subscription?.planId || 'free');

  const planEnabled = limits[flag] === true;
  const isGrandfathered = grandfathered.includes(flag);
  const enabled = planEnabled || isGrandfathered;

  const requiredPlan = FEATURE_MIN_PLAN[flag] || 'professional';
  const addon = FEATURE_ADDON[flag];
  const addonAvailable =
    !enabled &&
    !!addon &&
    planLevel(currentPlan) >= planLevel(addon.availableFrom) &&
    planLevel(currentPlan) < planLevel(requiredPlan);

  return {
    feature: flag,
    enabled,
    requiresUpgrade: !enabled,
    isGrandfathered,
    requiredPlan,
    addonAvailable,
    addonId: addonAvailable ? addon.id : null,
    currentPlan,
  };
};

/**
 * Features (flags) que un plan `targetPlan` desbloquea respecto de `currentPlan`.
 * Usado por la tabla comparativa del FeatureGateModal (variante upgrade_plan).
 * @returns {string[]} lista de flags ordenada por tier ascendente.
 */
export const featuresUnlockedBy = (targetPlan, currentPlan) => {
  const target = planLevel(targetPlan);
  const current = planLevel(currentPlan);
  return Object.keys(FEATURE_MIN_PLAN)
    .filter((flag) => {
      const lvl = planLevel(FEATURE_MIN_PLAN[flag]);
      return lvl > current && lvl <= target;
    })
    .sort((a, b) => planLevel(FEATURE_MIN_PLAN[a]) - planLevel(FEATURE_MIN_PLAN[b]));
};

/**
 * Dispara el modal de feature-gate de forma imperativa (pre-check del FE, sin pasar
 * por un 403). El FeatureGateHost escucha `FEATURE_GATE_EVENT` y abre el modal con
 * el mismo shape que el payload del 403 `FEATURE_GATE_REJECTED`.
 *
 * @param {object} payload - típicamente el resultado de `evaluateFeatureGate` o el
 *   body del 403. Debe incluir al menos `feature` (flag).
 */
export const openFeatureGate = (payload) => {
  if (typeof window === 'undefined' || !payload?.feature) return;
  window.dispatchEvent(new CustomEvent(FEATURE_GATE_EVENT, { detail: payload }));
};
